import React, { useId } from "react";
import { SimpleCheckboxControlled } from "../textInput/SimpleCheckbox";

export type SelectableSettingsField = { id: string; label: string };
export type SettingsFieldGroup = {
  id: string;
  label: string;
  fields: SelectableSettingsField[];
};
export type SettingsFieldStatus = { saving?: boolean; error?: string };
export type GroupedFieldSettingsProps = {
  title: string;
  description: string;
  groups: SettingsFieldGroup[];
  selection: Record<string, boolean>;
  statuses?: Record<string, SettingsFieldStatus>;
  onChange: (id: string, enabled: boolean) => void;
  disabled?: boolean;
  emptyMessage?: string;
};

/** Compact, controlled checkbox groups with independent autosave/error states. */
export function GroupedFieldSettings({
  title,
  description,
  groups,
  selection,
  statuses = {},
  onChange,
  disabled = false,
  emptyMessage = "No fields enabled for this form.",
}: GroupedFieldSettingsProps) {
  const instanceId = useId();
  return (
    <section className="tw-boundary" aria-label={title}>
      <h3 className="m-0 text-[14px] font-medium leading-[21px] text-neutral-800">{title}</h3>
      <p className="mb-0 mt-1 text-[14px] leading-[21px] text-gray-500">{description}</p>
      <div className="mt-3 space-y-4">
        {groups.map((group) => (
          <fieldset key={group.id} className="m-0 min-w-0 border-0 p-0" disabled={disabled}>
            <legend
              className={
                groups.length > 1 ? "mb-2 text-[14px] font-medium text-neutral-800" : "sr-only"
              }
            >
              {group.label}
            </legend>
            {group.fields.length ? (
              <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                {group.fields.map((field) => (
                  <div key={field.id} className="min-w-0 break-words">
                    <SimpleCheckboxControlled
                      name={`${instanceId}-${encodeURIComponent(field.id)}`}
                      label={field.label}
                      checked={selection[field.id] === true}
                      disabled={disabled || statuses[field.id]?.saving}
                      onChange={(enabled) => onChange(field.id, enabled)}
                    />
                    {statuses[field.id]?.error && (
                      <p className="mb-0 mt-1 text-xs text-red-600" role="alert">
                        {statuses[field.id].error}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="m-0 text-[14px] leading-[21px] text-gray-500" role="status">
                {emptyMessage}
              </p>
            )}
          </fieldset>
        ))}
      </div>
    </section>
  );
}
